import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Box, Typography, Grid, Card, CardContent, CardMedia, CardActions,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, CircularProgress, Tooltip,
  Fab, LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import StoreIcon from "@material-ui/icons/Store";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ImageIcon from "@material-ui/icons/Image";
import CloseIcon from "@material-ui/icons/Close";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0a0a0a 0%, #111827 50%, #0a0a0a 100%)",
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
  },
  header: { textAlign: "center", marginBottom: theme.spacing(5) },
  headerIcon: { fontSize: 48, color: "#01BBAC", marginBottom: theme.spacing(1) },
  headerTitle: {
    fontSize: "2.2rem", fontWeight: 800,
    background: "linear-gradient(135deg, #01BBAC 0%, #00e5d4 100%)",
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
    [theme.breakpoints.down("sm")]: { fontSize: "1.6rem" },
  },
  headerSub: { color: "rgba(255,255,255,0.5)", fontSize: "1rem", marginTop: theme.spacing(1) },
  card: {
    background: "linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)",
    border: "1px solid rgba(1,187,172,0.15)", borderRadius: 16,
    overflow: "hidden", height: "100%", display: "flex", flexDirection: "column",
    transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
    "&:hover": {
      transform: "translateY(-6px)",
      border: "1px solid rgba(1,187,172,0.45)",
      boxShadow: "0 20px 40px rgba(1,187,172,0.15)",
    },
  },
  cardMedia: { height: 200, objectFit: "cover" },
  cardMediaPlaceholder: {
    height: 200,
    background: "linear-gradient(135deg, #0f3460 0%, #533483 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  cardContent: { flex: 1, padding: theme.spacing(2.5) },
  cardTitle: { color: "#fff", fontWeight: 700, fontSize: "1.15rem", marginBottom: theme.spacing(1), lineHeight: 1.3 },
  cardDesc: {
    color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", lineHeight: 1.6,
    display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden",
  },
  cardActions: { padding: theme.spacing(0, 2.5, 2.5), gap: 8, flexWrap: "wrap" },
  btnSite: {
    background: "linear-gradient(135deg, #01BBAC 0%, #00e5d4 100%)",
    color: "#000", fontWeight: 700, borderRadius: 25, textTransform: "none", fontSize: "0.82rem", padding: "6px 16px",
    "&:hover": { opacity: 0.85 },
  },
  btnWpp: {
    background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
    color: "#fff", fontWeight: 700, borderRadius: 25, textTransform: "none", fontSize: "0.82rem", padding: "6px 16px",
    "&:hover": { opacity: 0.85 },
  },
  fab: {
    position: "fixed", bottom: 32, right: 32,
    background: "linear-gradient(135deg, #01BBAC, #00e5d4)", color: "#000",
    "&:hover": { opacity: 0.9 }, zIndex: 1000,
  },
  dialog: {
    "& .MuiPaper-root": {
      background: "#1a1a2e", border: "1px solid rgba(1,187,172,0.2)", borderRadius: 16,
      maxHeight: "90vh",
    },
  },
  dialogTitle: { color: "#01BBAC", fontWeight: 700 },
  input: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(1,187,172,0.3)" },
      "&:hover fieldset": { borderColor: "#01BBAC" },
      "&.Mui-focused fieldset": { borderColor: "#01BBAC" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#01BBAC" },
  },
  emptyState: { textAlign: "center", padding: theme.spacing(8), color: "rgba(255,255,255,0.3)" },
  adminBar: {
    background: "rgba(1,187,172,0.08)", border: "1px solid rgba(1,187,172,0.2)",
    borderRadius: 12, padding: theme.spacing(1.5, 2), marginBottom: theme.spacing(3),
    display: "flex", alignItems: "center", gap: 8,
  },
  cardWrapper: { position: "relative", height: "100%" },
  adminActions: { position: "absolute", top: 8, left: 8, display: "flex", gap: 4, zIndex: 2 },
  adminBtn: {
    background: "rgba(0,0,0,0.7)", color: "#fff", width: 30, height: 30,
    "&:hover": { background: "rgba(1,187,172,0.8)" },
  },
  // Image upload area
  uploadArea: {
    border: "2px dashed rgba(1,187,172,0.4)", borderRadius: 12,
    padding: theme.spacing(3), textAlign: "center", cursor: "pointer",
    marginBottom: theme.spacing(2), transition: "all 0.2s",
    "&:hover": { border: "2px dashed #01BBAC", background: "rgba(1,187,172,0.05)" },
  },
  uploadAreaActive: {
    border: "2px solid #01BBAC", background: "rgba(1,187,172,0.08)",
  },
  previewBox: {
    position: "relative", borderRadius: 12, overflow: "hidden",
    marginBottom: theme.spacing(2), border: "1px solid rgba(1,187,172,0.3)",
  },
  previewImg: { width: "100%", maxHeight: 220, objectFit: "cover", display: "block" },
  previewOverlay: {
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
    justifyContent: "center", opacity: 0, transition: "opacity 0.2s",
    "&:hover": { opacity: 1 },
  },
  removeImgBtn: {
    position: "absolute", top: 8, right: 8,
    background: "rgba(0,0,0,0.7)", color: "#fff",
    "&:hover": { background: "#ff6b6b" },
  },
}));

const EMPTY_FORM = { title: "", description: "", imageUrl: "", siteUrl: "", whatsappUrl: "", order: 0, active: true };

export default function EstratizaServices() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isMaster = user?.super === true;
  const fileRef = useRef(null);

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(""); // local blob or saved URL
  const [dragOver, setDragOver] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = isMaster ? "/estratiza-services/all" : "/estratiza-services";
      const { data } = await api.get(endpoint);
      setServices(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null); setForm(EMPTY_FORM); setPreviewSrc(""); setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ title: s.title, description: s.description || "", imageUrl: s.imageUrl || "", siteUrl: s.siteUrl || "", whatsappUrl: s.whatsappUrl || "", order: s.order || 0, active: s.active !== false });
    setPreviewSrc(s.imageUrl || "");
    setOpen(true);
  };

  const handleFileSelect = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Selecione uma imagem válida"); return; }
    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPreviewSrc(localUrl);

    // Upload to server
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/estratiza-services/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total || file.size))),
      });
      setForm((f) => ({ ...f, imageUrl: data.imageUrl }));
      setPreviewSrc(data.imageUrl);
      toast.success("Imagem carregada!");
    } catch (e) {
      toast.error("Erro ao fazer upload da imagem");
      setPreviewSrc("");
      setForm((f) => ({ ...f, imageUrl: "" }));
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const removeImage = () => {
    setPreviewSrc(""); setForm((f) => ({ ...f, imageUrl: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Título é obrigatório"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/estratiza-services/${editing.id}`, form);
        toast.success("Serviço atualizado!");
      } else {
        await api.post("/estratiza-services", form);
        toast.success("Serviço criado!");
      }
      setOpen(false); load();
    } catch (e) { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/estratiza-services/${deleteTarget.id}`);
      toast.success("Serviço removido"); setDeleteTarget(null); load();
    } catch (e) { toast.error("Erro ao remover"); }
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <StoreIcon className={classes.headerIcon} />
        <Typography className={classes.headerTitle}>Serviços Estratiza</Typography>
        <Typography className={classes.headerSub}>Soluções e ferramentas para potencializar seu negócio</Typography>
      </Box>

      {isMaster && (
        <Box className={classes.adminBar}>
          <span style={{ fontSize: 18 }}>⚡</span>
          <Typography style={{ color: "#01BBAC", fontSize: "0.85rem", fontWeight: 600 }}>
            Modo Admin Master — adicione, edite e remova serviços
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress style={{ color: "#01BBAC" }} />
        </Box>
      ) : services.length === 0 ? (
        <Box className={classes.emptyState}>
          <StoreIcon style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }} />
          <Typography variant="h6" style={{ color: "rgba(255,255,255,0.3)" }}>
            {isMaster ? "Nenhum serviço cadastrado ainda. Clique em + para adicionar." : "Nenhum serviço disponível no momento."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {services.map((s) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={s.id}>
              <Box className={classes.cardWrapper}>
                {isMaster && (
                  <Box className={classes.adminActions}>
                    <Tooltip title="Editar"><IconButton className={classes.adminBtn} size="small" onClick={() => openEdit(s)}><EditIcon style={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton className={classes.adminBtn} size="small" onClick={() => setDeleteTarget(s)}><DeleteIcon style={{ fontSize: 16 }} /></IconButton></Tooltip>
                  </Box>
                )}
                <Card className={classes.card} style={{ opacity: !s.active && isMaster ? 0.6 : 1 }}>
                  {s.imageUrl ? (
                    <CardMedia component="img" image={s.imageUrl} alt={s.title} className={classes.cardMedia} />
                  ) : (
                    <Box className={classes.cardMediaPlaceholder}>
                      <StoreIcon style={{ fontSize: 56, color: "rgba(255,255,255,0.2)" }} />
                    </Box>
                  )}
                  <CardContent className={classes.cardContent}>
                    <Typography className={classes.cardTitle}>{s.title}</Typography>
                    {s.description && <Typography className={classes.cardDesc}>{s.description}</Typography>}
                  </CardContent>
                  {(s.siteUrl || s.whatsappUrl) && (
                    <CardActions className={classes.cardActions}>
                      {s.siteUrl && (
                        <Button className={classes.btnSite} size="small" startIcon={<OpenInNewIcon style={{ fontSize: 14 }} />} onClick={() => window.open(s.siteUrl, "_blank")}>
                          Saiba mais
                        </Button>
                      )}
                      {s.whatsappUrl && (
                        <Button className={classes.btnWpp} size="small" startIcon={<WhatsAppIcon style={{ fontSize: 14 }} />} onClick={() => window.open(s.whatsappUrl, "_blank")}>
                          Falar no WhatsApp
                        </Button>
                      )}
                    </CardActions>
                  )}
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {isMaster && (
        <Tooltip title="Adicionar serviço">
          <Fab className={classes.fab} onClick={openNew}><AddIcon /></Fab>
        </Tooltip>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth className={classes.dialog}>
        <DialogTitle className={classes.dialogTitle}>
          {editing ? "Editar Serviço" : "Novo Serviço"}
        </DialogTitle>
        <DialogContent style={{ overflowY: "auto" }}>

          {/* IMAGE UPLOAD */}
          <Typography style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", marginBottom: 8 }}>
            IMAGEM DO CARD
          </Typography>

          {/* Preview */}
          {previewSrc ? (
            <Box className={classes.previewBox}>
              <img src={previewSrc} alt="preview" className={classes.previewImg} />
              <Box className={classes.previewOverlay}>
                <VisibilityIcon style={{ color: "#fff", fontSize: 32 }} />
              </Box>
              <IconButton className={classes.removeImgBtn} size="small" onClick={removeImage}>
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
              {uploading && (
                <Box style={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} style={{ background: "rgba(255,255,255,0.2)" }} />
                </Box>
              )}
            </Box>
          ) : (
            <Box
              className={`${classes.uploadArea} ${dragOver ? classes.uploadAreaActive : ""}`}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              {uploading ? (
                <Box>
                  <CircularProgress size={32} style={{ color: "#01BBAC", marginBottom: 8 }} />
                  <Typography style={{ color: "#01BBAC", fontSize: "0.85rem" }}>
                    Enviando... {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} style={{ marginTop: 8, borderRadius: 4 }} />
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon style={{ fontSize: 40, color: "rgba(1,187,172,0.6)", marginBottom: 8 }} />
                  <Typography style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem" }}>
                    Clique para selecionar ou arraste a imagem aqui
                  </Typography>
                  <Typography style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.75rem", marginTop: 4 }}>
                    JPG, PNG, GIF, WebP
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => { if (e.target.files[0]) handleFileSelect(e.target.files[0]); }}
          />

          {previewSrc && !uploading && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => fileRef.current?.click()}
              style={{ color: "#01BBAC", borderColor: "rgba(1,187,172,0.4)", borderRadius: 8, marginBottom: 16, textTransform: "none" }}
            >
              Trocar imagem
            </Button>
          )}

          <Box mt={1} />

          {/* Form fields */}
          <TextField className={classes.input} label="Título *" variant="outlined" fullWidth
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <TextField className={classes.input} label="Descrição" variant="outlined" fullWidth multiline rows={4}
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Descreva o serviço/produto de forma atrativa..." />
          <TextField className={classes.input} label="Link do site" variant="outlined" fullWidth
            value={form.siteUrl} onChange={(e) => setForm({ ...form, siteUrl: e.target.value })}
            placeholder="https://seusite.com.br" />
          <TextField className={classes.input} label="Link do WhatsApp" variant="outlined" fullWidth
            value={form.whatsappUrl} onChange={(e) => setForm({ ...form, whatsappUrl: e.target.value })}
            placeholder="https://wa.me/5511999999999" />
          <TextField className={classes.input} label="Ordem de exibição" variant="outlined" type="number"
            value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} style={{ width: 180 }} />
          <Box mt={1}>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} style={{ color: "#01BBAC" }} />}
              label={<Typography style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>Visível para clientes</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={() => setOpen(false)} style={{ color: "rgba(255,255,255,0.5)" }}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || uploading}
            style={{ background: uploading ? "rgba(1,187,172,0.4)" : "linear-gradient(135deg,#01BBAC,#00e5d4)", color: "#000", fontWeight: 700, borderRadius: 25, padding: "6px 24px" }}>
            {saving ? <CircularProgress size={20} /> : (editing ? "Salvar" : "Criar")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} className={classes.dialog}>
        <DialogTitle style={{ color: "#ff6b6b" }}>Remover Serviço</DialogTitle>
        <DialogContent>
          <Typography style={{ color: "rgba(255,255,255,0.7)" }}>
            Tem certeza que deseja remover <strong style={{ color: "#fff" }}>{deleteTarget?.title}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={() => setDeleteTarget(null)} style={{ color: "rgba(255,255,255,0.5)" }}>Cancelar</Button>
          <Button onClick={handleDelete} style={{ background: "#ff6b6b", color: "#fff", fontWeight: 700, borderRadius: 25, padding: "6px 24px" }}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
